import type { AvailabilityWindow } from "@/components/availability-picker";
import i18n from "@/i18n";
import {
  searchShifts,
  searchShiftsByAvailability,
  searchWorkers,
} from "@/queries/api/searchApi";
import type { Profile } from "@/schemas/profile";
import type { ShiftResponse } from "@/schemas/shift";
import { localeFor } from "@/utils/formatters";
import type { Role } from "@/utils/hooks/use-role";

import type { UIShift } from "../-components/shift-details"; // UIShift is a thin UI shape derived from ShiftResponse

import { ROLE_OPTIONS as roles } from "@/constants/roles";

// Employer worker search defaults to a broad 50-mile radius of the restaurant;
// the worker shift search keeps its tighter 20-mile default. Exported so the
// Search page can label the results ("workers within 50 miles") with the same
// value the payload uses.
export const EMPLOYER_SEARCH_RADIUS_MILES = 50;
export const WORKER_SEARCH_PAGE_SIZE = 30;

export function shiftTimesFromRange(startStr?: string, endStr?: string) {
  function parseHourFromString(s: string | undefined | null): number | null {
    if (!s) return null;
    const str = s.trim();
    const m = str.match(/^\d{1,2}(?::\d{2})?\s*[AaPp][Mm]?$/);
    if (!m) {
      const m2 = str.match(/(\d{1,2}):?(\d{2})?/);
      if (!m2) return null;
      const h = Number(m2[1]);
      return isNaN(h) ? null : h % 24;
    }
    let hour = Number(str.match(/\d{1,2}/)![0]);
    const mer = str.match(/[AaPp][Mm]/)?.[0];
    if (mer) {
      const lower = mer.toLowerCase();
      if (lower === "am") {
        if (hour === 12) hour = 0;
      } else if (lower === "pm") {
        if (hour !== 12) hour = (hour % 12) + 12;
      }
    }
    return hour % 24;
  }
  function hoursRange(start: number, end: number): Array<number> {
    const hours: Array<number> = [];
    if (start === end) {
      for (let h = 0; h < 24; h++) hours.push(h);
      return hours;
    }
    if (start < end) {
      for (let h = start; h < end; h++) hours.push(h % 24);
      return hours;
    }
    for (let h = start; h < 24; h++) hours.push(h);
    for (let h = 0; h < end; h++) hours.push(h);
    return hours;
  }
  function mapHoursToShiftTimes(hours: Array<number>): Array<string> {
    const set = new Set<string>();
    for (const h of hours) {
      if (h >= 0 && h < 12) set.add("MORNING");
      else if (h >= 12 && h < 18) set.add("AFTERNOON");
      else if (h >= 18 && h < 24) set.add("EVENING");
    }
    return Array.from(set);
  }
  const start = parseHourFromString(startStr ?? null);
  const end = parseHourFromString(endStr ?? null);
  if (start == null || end == null) return [];
  const hrs = hoursRange(start, end);
  return mapHoursToShiftTimes(hrs);
}

export function coerceCenter(val: any): google.maps.LatLngLiteral | undefined {
  if (!val) return undefined;
  if (typeof val.lat === "number" && typeof val.lng === "number") {
    return { lat: val.lat, lng: val.lng };
  }
  if (
    Array.isArray(val) &&
    val.length === 2 &&
    typeof val[0] === "number" &&
    typeof val[1] === "number"
  ) {
    return { lat: val[0], lng: val[1] };
  }
  if (typeof val === "object" && val !== null) {
    if (
      "latitude" in val &&
      "longitude" in val &&
      typeof val.latitude === "number" &&
      typeof val.longitude === "number"
    ) {
      return { lat: val.latitude, lng: val.longitude };
    }
  }
  return undefined;
}

export function computeCenterFromShifts<T extends { lat: number; lng: number }>(
  list: Array<T>,
): google.maps.LatLngLiteral {
  if (!list.length) return { lat: 34.0522, lng: -118.2437 };
  const lat = list.reduce((sum, s) => sum + s.lat, 0) / list.length;
  const lng = list.reduce((sum, s) => sum + s.lng, 0) / list.length;
  return { lat, lng };
}

// Map API shift response to UI shift for display
export function mapApiShiftToUIShift(api: ShiftResponse | any): UIShift {
  // Defensive: handle both ShiftResponse and assignment-wrapped shift
  const s = api.shift ? api.shift : api;
  // Format date (e.g. "Thu, May 14")
  const date = s.start_date
    ? new Date(s.start_date + "T00:00:00").toLocaleDateString(
        localeFor(i18n.language || "en"),
        {
          weekday: "short",
          month: "short",
          day: "numeric",
        },
      )
    : undefined;
  let time = undefined;
  if (s.start_time && s.end_time) {
    // Format times to 12-hour AM/PM format
    function formatTimeTo12Hour(timeString: string): string {
      const [hours, minutes] = timeString.split(":").map(Number);
      if (isNaN(hours) || isNaN(minutes)) return timeString;

      const period = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM

      return `${displayHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}${period}`;
    }

    const startTime = formatTimeTo12Hour(s.start_time);
    const endTime = formatTimeTo12Hour(s.end_time);
    time = `${startTime} – ${endTime}`;
  }
  return {
    id: String(s.id),
    title: s.name || s.role_name || s.title || "Shift",
    venue: s.location || s.venue || (s.address && s.address.street) || "Venue",
    lat: s.latitude || (s.address && s.address.latitude) || 0,
    lng: s.longitude || (s.address && s.address.longitude) || 0,
    date,
    time,
    startDate: s.start_date || undefined,
    rate: s.wage || 0,
    total: typeof s.total === "number" ? s.total : undefined,
    notes: s.notes || s.role_description || undefined,
    instant: s.allow_instant_acceptance || s.instant || false,
    offer: !!api.offer,
    allow_instant_acceptance: s.allow_instant_acceptance,
    require_training: s.require_training,
    business: {
      id: s.business?.id,
      user_id: s.business?.user_id,
      name: s.business?.name || s.business_name || s.employer_name || undefined,
    },
    role_name: s.role_name,
    seriesId: s.series_id ?? undefined,
  };
}

// A restaurant marker on the map: one business at one location, with the
// shifts it currently has available grouped underneath it.
export type RestaurantGroup = {
  key: string;
  name: string;
  venue: string;
  lat: number;
  lng: number;
  shifts: Array<UIShift>;
};

function startDateMs(isoDate: string | undefined): number {
  if (!isoDate) return Number.POSITIVE_INFINITY; // undated shifts sort last
  const t = new Date(isoDate + "T00:00:00").getTime();
  return isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

// True when `isoDate` falls within the next `days` days (inclusive of today).
// Defensive: shifts with a missing/unparseable date are kept rather than hidden.
export function isWithinNextDays(
  isoDate: string | undefined,
  days: number,
): boolean {
  if (!isoDate) return true;
  const d = new Date(isoDate + "T00:00:00");
  if (isNaN(d.getTime())) return true;
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(start);
  end.setDate(end.getDate() + days);
  return d.getTime() >= start.getTime() && d.getTime() <= end.getTime();
}

// Collapse the flat shift list into one entry per restaurant (business + venue
// coordinates) so the map shows a single pin per location. When `withinDays` is
// set, each group only keeps shifts starting within that window, and groups left
// with no shifts are dropped. Shifts within a group are sorted soonest-first.
export function groupShiftsByRestaurant(
  shifts: Array<UIShift>,
  opts?: { withinDays?: number },
): Array<RestaurantGroup> {
  const withinDays = opts?.withinDays;
  const groups = new Map<string, RestaurantGroup>();

  for (const s of shifts) {
    const name = s.business?.name ?? "";
    const identity = s.business?.id ?? (name || s.venue);
    const key = `${identity}|${s.lat.toFixed(4)}|${s.lng.toFixed(4)}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        name,
        venue: s.venue,
        lat: s.lat,
        lng: s.lng,
        shifts: [],
      };
      groups.set(key, group);
    }
    group.shifts.push(s);
  }

  const result: Array<RestaurantGroup> = [];
  for (const group of groups.values()) {
    const list = (
      typeof withinDays === "number"
        ? group.shifts.filter((s) => isWithinNextDays(s.startDate, withinDays))
        : group.shifts
    )
      .slice()
      .sort((a, b) => startDateMs(a.startDate) - startDateMs(b.startDate));
    if (!list.length) continue;
    result.push({ ...group, shifts: list });
  }
  return result;
}

export function getRoleLabelFromValue(
  value: string | undefined,
): string | undefined {
  if (!value) return undefined;
  const found = (roles as Array<{ label: string; value: string }>).find(
    (r) => r.value === value,
  );
  return found?.label;
}

type BuildSearchOptions = {
  center?: google.maps.LatLngLiteral | undefined;
  radiusMiles?: number | undefined;
  role_names?: Array<string> | undefined;
  when?: string | undefined;
  startTime?: string | undefined;
  endTime?: string | undefined;
  days?: Array<string> | undefined;
  windows?: Array<AvailabilityWindow> | undefined;
  requiresExperience?: boolean | undefined;
  page?: number | undefined;
  /** Worker search only: server-side column sort (defaults to distance) + text filter. */
  sortBy?: string | undefined;
  sortDir?: "asc" | "desc" | undefined;
  query?: string | undefined;
};

export function buildSearchPayload(
  opts: BuildSearchOptions & { searchType?: "shift" | "worker" },
) {
  const {
    center,
    radiusMiles,
    role_names,
    when,
    startTime,
    endTime,
    days,
    windows,
    searchType,
    requiresExperience,
    page,
    sortBy,
    sortDir,
    query,
  } = opts;
  const payload: any = {};
  if (center)
    payload.location = { latitude: center.lat, longitude: center.lng };
  if (typeof radiusMiles === "number") payload.distance = radiusMiles;
  if (Array.isArray(role_names) && role_names.length) {
    payload.role_names = role_names;
  }

  if (searchType === "worker" && requiresExperience === true) {
    payload.requires_experience = true;
  }

  if (searchType === "worker") {
    // Nearest-first by default: surfaces the closest workers and keeps the first
    // page a representative sample of the whole radius (not clustered by insert
    // order). A column sort / text filter from the Search table is applied by the
    // backend so it spans every page, not just the rows loaded so far.
    payload.sort_by = sortBy ?? "distance";
    if (sortBy && sortDir) payload.sort_dir = sortDir;
    const trimmedQuery = query?.trim();
    if (trimmedQuery) payload.query = trimmedQuery;
  }

  if (searchType === "worker" && Array.isArray(windows) && windows.length) {
    payload.windows = windows;
  }

  if (Array.isArray(days) && days.length) {
    payload.shift_days = days;
  }

  const explicitTimes = shiftTimesFromRange(startTime, endTime);
  if (explicitTimes.length) {
    payload.shift_times = explicitTimes;
  } else if (!payload.shift_days && !payload.windows) {
    if (when === "today") {
      const today = new Date()
        .toLocaleDateString("en-US", { weekday: "long" })
        .toUpperCase();
      if (today) payload.shift_days = [today];
    } else if (when === "weekend") {
      payload.shift_days = ["SATURDAY", "SUNDAY"];
    }
  }

  // Employer worker search defaults to a broad 50-mile radius of the restaurant;
  // the worker shift search keeps its tighter 20-mile default.
  const defaultDistance =
    searchType === "worker" ? EMPLOYER_SEARCH_RADIUS_MILES : 20;
  const distance =
    typeof radiusMiles === "number" && !isNaN(radiusMiles)
      ? radiusMiles
      : defaultDistance;
  return {
    ...payload,
    page: typeof page === "number" && page > 0 ? page : 0,
    page_size: WORKER_SEARCH_PAGE_SIZE,
    distance,
  };
}

export function buildAvailabilityPayload(opts: {
  windows: Array<AvailabilityWindow>;
  center?: google.maps.LatLngLiteral | undefined;
  radiusMiles?: number | undefined;
  role_names?: Array<string> | undefined;
}) {
  const { windows, center, radiusMiles, role_names } = opts;
  const distance =
    typeof radiusMiles === "number" && !isNaN(radiusMiles) ? radiusMiles : 20;
  return {
    windows,
    ...(center
      ? { location: { latitude: center.lat, longitude: center.lng } }
      : {}),
    ...(role_names && role_names.length ? { role_names } : {}),
    distance,
    page: 0,
    page_size: 30,
  };
}

function parseRoleNames(searchParams: any): Array<string> | undefined {
  const plural = searchParams.role_names;
  if (typeof plural === "string" && plural.length) {
    return plural.split(",").map((s: string) => s.trim()).filter(Boolean);
  }
  if (Array.isArray(plural) && plural.length) {
    return plural.filter((v: any): v is string => typeof v === "string");
  }
  const singular = searchParams.role_name;
  return typeof singular === "string" && singular.length ? [singular] : undefined;
}

function parseWindows(searchParams: any): Array<AvailabilityWindow> | undefined {
  const raw = searchParams.windows;
  if (!raw) return undefined;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed) && parsed.length) return parsed;
  } catch {
    /* ignore */
  }
  return undefined;
}

export async function performSearch({
  role,
  searchParams,
  page,
  searchShiftsImpl = searchShifts,
  searchShiftsByAvailabilityImpl = searchShiftsByAvailability,
  searchWorkersImpl = searchWorkers,
}: {
  role: Role;
  searchParams: any;
  page?: number;
  searchShiftsImpl?: typeof searchShifts;
  searchShiftsByAvailabilityImpl?: typeof searchShiftsByAvailability;
  searchWorkersImpl?: typeof searchWorkers;
}): Promise<{
  shifts: Array<ReturnType<typeof mapApiShiftToUIShift>>;
  users: Array<Profile>;
  total: number | undefined;
  center: google.maps.LatLngLiteral | undefined;
  miles: number | undefined;
  role_name: string | undefined;
  when: string | undefined;
}> {
  const roleNames = parseRoleNames(searchParams);
  const roleName = roleNames?.[0];
  const when = searchParams.when;
  const latStr = searchParams.lat;
  const lngStr = searchParams.lng;
  const radiusMiles =
    typeof searchParams.radiusMiles === "number"
      ? searchParams.radiusMiles
      : typeof searchParams.radiusMiles === "string"
        ? Number(searchParams.radiusMiles)
        : undefined;
  const lat = latStr != null ? Number(latStr) : undefined;
  const lng = lngStr != null ? Number(lngStr) : undefined;
  const centerFromQuery = lat != null && lng != null ? { lat, lng } : undefined;
  const windows = parseWindows(searchParams);
  const requiresExperience =
    searchParams.requiresExperience === true ||
    searchParams.requiresExperience === "true";

  if (role === "worker" && windows) {
    const payload = buildAvailabilityPayload({
      windows,
      center: centerFromQuery,
      radiusMiles,
      role_names: roleNames,
    });
    const res = await searchShiftsByAvailabilityImpl(payload);
    const uiShifts = res.shifts.map((s: any) => mapApiShiftToUIShift(s));
    let center: google.maps.LatLngLiteral | undefined;
    if (centerFromQuery) center = centerFromQuery;
    else if (uiShifts.length) center = computeCenterFromShifts(uiShifts);
    return {
      shifts: uiShifts,
      users: [],
      total: undefined,
      center,
      miles: radiusMiles,
      role_name: roleName,
      when,
    };
  }

  const shiftDaysRaw = searchParams.shiftDays;
  const days =
    typeof shiftDaysRaw === "string" && shiftDaysRaw.length
      ? shiftDaysRaw.split(",").map((s: string) => s.trim().toUpperCase())
      : undefined;
  const payload: any = buildSearchPayload({
    center: centerFromQuery,
    radiusMiles,
    role_names: roleNames,
    when,
    startTime: searchParams.startTime,
    endTime: searchParams.endTime,
    days,
    windows,
    searchType: role === "worker" ? "shift" : "worker",
    requiresExperience: requiresExperience || undefined,
    page,
    sortBy: typeof searchParams.sort === "string" ? searchParams.sort : undefined,
    sortDir:
      searchParams.dir === "asc" || searchParams.dir === "desc"
        ? searchParams.dir
        : undefined,
    query: typeof searchParams.q === "string" ? searchParams.q : undefined,
  });

  if (
    !(
      (payload.role_names && payload.role_names.length) ||
      payload.location ||
      (payload.shift_times && payload.shift_times.length) ||
      (payload.shift_days && payload.shift_days.length) ||
      (payload.windows && payload.windows.length) ||
      typeof payload.distance === "number"
    )
  ) {
    return {
      shifts: [],
      users: [],
      total: undefined,
      center: undefined,
      miles: radiusMiles,
      role_name: roleName,
      when,
    };
  }
  if (role === "worker") {
    const res = await searchShiftsImpl({
      ...payload,
      status: "PUBLISHED",
    });
    const apiShifts = res.shifts;
    const uiShifts = apiShifts.map((s: any) => mapApiShiftToUIShift(s));
    let center: google.maps.LatLngLiteral | undefined;
    if (centerFromQuery) center = centerFromQuery;
    else if (uiShifts.length) center = computeCenterFromShifts(uiShifts);
    return {
      shifts: uiShifts,
      users: [],
      total: undefined,
      center,
      miles: radiusMiles,
      role_name: roleName,
      when,
    };
  } else {
    const res = await searchWorkersImpl(payload);
    const users = res.users;
    const center = centerFromQuery;
    return {
      shifts: [],
      users,
      total: res.total,
      center,
      miles: radiusMiles,
      role_name: roleName,
      when,
    };
  }
}
